import { type PricingTierId } from './usage-credits';

/**
 * Usage-Credit (UC) tier configuration — the ADR-0149 composite-usage model's
 * tier table: list price, the monthly **included UC allowance**, and the per-tier
 * seat cap. This is the LIVE pricing model: `getUcTier(tier).includedUsageCredits`
 * is the allowance the per-turn UC gate enforces, and the API's
 * `billing-tier-catalog.ts` carries the matching display prices (drift-guarded).
 *
 * Names kept (Starter/Growth/Scale/Enterprise); no free tier — Starter is the
 * paid entry tier. Provisional, launch-locked numbers;
 * recalibrated against live COGS at the first ~10 paying tenants. The effective
 * $/UC each tier buys is `UC_INCLUDED_RATE_USD` from the engine — this table's
 * `monthlyPriceUsd / includedUsageCredits` reproduces that ladder (asserted in
 * the tests), so the tier config can never silently drift off the margin floor.
 *
 * `-1` = "custom / contractual" (Enterprise — a negotiated committed UC block,
 * no fixed self-serve allowance), matching the MAEU catalog's `-1` convention.
 *
 * Pure-TS, zero I/O.
 */

export interface UcTier {
  readonly tier: PricingTierId;
  readonly displayName: string;
  /** Monthly list price USD; `null` for Enterprise (negotiated, no published number). */
  readonly monthlyPriceUsd: number | null;
  /**
   * Included Usage Credits per month — the monthly MINIMUM the tier buys at its
   * discounted `UC_INCLUDED_RATE_USD`. `-1` for Enterprise (committed block).
   */
  readonly includedUsageCredits: number;
  /** Operator/team seats included; `-1` for Enterprise (custom). */
  readonly seatCap: number;
}

export const UC_TIERS: Readonly<Record<PricingTierId, UcTier>> = {
  starter: {
    tier: 'starter',
    displayName: 'Starter',
    monthlyPriceUsd: 299,
    includedUsageCredits: 30_000,
    seatCap: 5,
  },
  growth: {
    tier: 'growth',
    displayName: 'Growth',
    monthlyPriceUsd: 999,
    includedUsageCredits: 110_000,
    seatCap: 10,
  },
  scale: {
    tier: 'scale',
    displayName: 'Scale',
    monthlyPriceUsd: 2_999,
    includedUsageCredits: 360_000,
    seatCap: 25,
  },
  enterprise: {
    tier: 'enterprise',
    displayName: 'Enterprise',
    monthlyPriceUsd: null,
    includedUsageCredits: -1,
    seatCap: -1,
  },
};

export const UC_TIER_IDS: readonly PricingTierId[] = ['starter', 'growth', 'scale', 'enterprise'];

/** Resolve a UC tier's config. Throws on an unknown slug. */
export function getUcTier(tier: PricingTierId): UcTier {
  if (!(tier in UC_TIERS)) {
    throw new Error(`Unknown UC tier: ${tier}`);
  }
  return UC_TIERS[tier];
}

/**
 * The effective included $/UC a self-serve tier buys (`price / includedCU`).
 * Returns `null` for Enterprise (no published price/allowance). This is what
 * must stay on the `UC_INCLUDED_RATE_USD` ladder ≥ the margin floor.
 */
export function effectiveIncludedRateUsd(tier: PricingTierId): number | null {
  const t = getUcTier(tier);
  if (t.monthlyPriceUsd === null || t.includedUsageCredits <= 0) {
    return null;
  }
  return t.monthlyPriceUsd / t.includedUsageCredits;
}
